import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Search, Sparkles, MessageCircleQuestion, BookOpen, Bot, TrendingUp } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { BOOK_HREF } from "@/data/solutions";

const TEAL = "#22D3C5";
const SBOX = { boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.2)" };

function SeoSystem() {
  const rows = [
    { Icon: MessageCircleQuestion, label: "Kundens spørgsmål", note: "Intent" },
    { Icon: BookOpen, label: "Jeres viden", note: "Indhold" },
    { Icon: Search, label: "Søgemaskiner", note: "Google" },
    { Icon: Bot, label: "AI-svar", note: "GEO" },
  ];
  return (
    <div aria-hidden className="rounded-2xl border border-border bg-card p-5 sm:p-6 select-none" style={SBOX}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
            <span className="relative h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />
          </span>
          <span className="text-[13px] font-medium text-foreground">Synlighedssystem</span>
        </div>
        <span className="rounded-full border border-border px-3 py-1 text-[11px] tracking-wide text-muted-foreground">Sporer</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {rows.map((r) => (
          <div key={r.label} className="rounded-xl border border-border bg-secondary/60 p-3.5">
            <r.Icon className="h-4 w-4 mb-2" style={{ color: TEAL }} strokeWidth={1.75} />
            <p className="text-[12.5px] text-foreground leading-tight">{r.label}</p>
            <p className="text-[10.5px] uppercase tracking-wider text-muted-foreground/70 mt-1">{r.note}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3 rounded-xl border px-3.5 py-3" style={{ borderColor: `${TEAL}59`, backgroundColor: `${TEAL}12` }}>
        <TrendingUp className="h-4 w-4 shrink-0" style={{ color: TEAL }} strokeWidth={1.75} />
        <span className="text-[13px] font-medium text-foreground">Relevant trafik &amp; henvendelser</span>
      </div>
    </div>
  );
}


const GET = [
  "Klassisk SEO til Google",
  "GEO (Generative Engine Optimization) - synlighed i AI-søgemaskiner",
  "AI-synlighed i ChatGPT, Perplexity, Gemini og Googles AI-svar",
  "Indhold, der besvarer kundernes spørgsmål klart",
  "Struktur, som gør virksomheden lettere at forstå for både mennesker og AI",
  "Teknisk SEO og løbende optimering",
];

export default function SeoPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: "#22D3C5", opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22D3C5" }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">SEO/GEO</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
            Bliv fundet af de rigtige kunder - <span style={{ color: "#22D3C5" }}>også i AI-søgninger</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
            <p>SEO handler ikke længere kun om Google. Flere kunder bruger AI-værktøjer til at finde svar, sammenligne leverandører og forstå deres muligheder. Derfor hjælper vi jer med at blive synlige både i klassiske søgemaskiner og i de nye AI-drevne søgeoplevelser.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <MButton href={BOOK_HREF} size="lg" data-testid="hero-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
            <MButton href="#hvad-faar-i" size="lg" variant="secondary" data-testid="hero-hvad-faar-i">Se hvad I får</MButton>
          </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <SeoSystem />
          </motion.div>
          </div>
        </div>
      </section>

      {/* SEO vs GEO */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16">
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">SEO og GEO</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Hvad er forskellen på SEO og GEO?</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-border bg-card p-7">
            <div className="inline-flex h-10 w-10 rounded-xl bg-secondary items-center justify-center mb-5">
              <Search className="h-5 w-5 text-foreground" strokeWidth={1.75} />
            </div>
            <h3 className="font-display text-xl font-medium mb-3">SEO - Search Engine Optimization</h3>
            <p className="text-muted-foreground leading-relaxed">
              Den klassiske disciplin: at rangere højt i søgemaskinerne, så I bliver fundet, når kunderne søger på Google. Handler om søgeord, indhold, teknik og struktur.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-7">
            <div className="inline-flex h-10 w-10 rounded-xl items-center justify-center mb-5" style={{ backgroundColor: "#22D3C522" }}>
              <Sparkles className="h-5 w-5" strokeWidth={1.75} style={{ color: "#22D3C5" }} />
            </div>
            <h3 className="font-display text-xl font-medium mb-3">GEO - Generative Engine Optimization</h3>
            <p className="text-muted-foreground leading-relaxed">
              Den nye disciplin: at blive nævnt, citeret og anbefalet, når kunderne spørger en AI-tjeneste som ChatGPT, Perplexity, Gemini eller Googles AI-svar. I dag starter flere og flere købsrejser i en AI - og GEO sikrer, at I er synlige dér, ikke kun på Google.
            </p>
          </div>
        </div>
      </section>

      {/* Hvad får I? */}
      <section id="hvad-faar-i" className="relative z-10 border-t border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvad får I?</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Synlighed på tværs af Google og AI-søgning</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {GET.map((g, i) => (
              <motion.div key={g} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 2) * 0.06 }}
                className="flex items-center gap-3 rounded-xl border border-border bg-background px-5 py-4">
                <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0">
                  <Check className="h-3.5 w-3.5 text-background" strokeWidth={2.5} />
                </div>
                <span className="text-foreground">{g}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kombinér med Aicom AI */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex h-12 w-12 rounded-xl bg-secondary items-center justify-center mb-6">
          <Sparkles className="h-5 w-5 text-foreground" strokeWidth={1.75} />
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-5">Kombinér med Aicom AI</h2>
        <div className="space-y-4 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          <p>Ønsker I selv at producere mere indhold, kan SEO kombineres med Aicom AI. Platformen hjælper med at skrive søgeoptimeret indhold, besvare kundernes spørgsmål klart og skabe struktur, der gør jer lettere at forstå for både mennesker og AI.</p>
        </div>
        <div className="mt-8">
          <MButton to="/losninger/aicom-ai" variant="secondary" data-testid="link-aicom-ai">Læs om Aicom AI <ArrowRight className="h-4 w-4" /></MButton>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
